import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, ExternalLink, Key, Eye, EyeOff, Check, AlertCircle } from 'lucide-react';
import { EcommercePlatform } from '../hooks/useEcommercePlatforms';
import oauthService from '../services/oauthService';

interface PlatformConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  platform: EcommercePlatform | null;
  onConnectionSuccess: (platformId: string, credentials: any) => void;
  onConnectionError?: () => void;
}

const PlatformConnectionModal: React.FC<PlatformConnectionModalProps> = ({
  isOpen,
  onClose,
  platform,
  onConnectionSuccess,
  onConnectionError
}) => {
  const [connectionType, setConnectionType] = useState<'oauth' | 'api_key'>('oauth');
  const [credentials, setCredentials] = useState({
    apiKey: '',
    clientId: '',
    clientSecret: '',
    accessToken: '',
    refreshToken: '',
    domain: '',
    shopDomain: '',
    accountId: '',
    storeHash: ''
  });
  const [showSecrets, setShowSecrets] = useState({
    apiKey: false,
    clientSecret: false,
    accessToken: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Configuración de cada plataforma
  const platformConfigs = {
    mercadolibre: {
      name: 'Mercado Libre',
      supportsOAuth: true,
      oauthUrl: 'https://auth.mercadolibre.com/authorization',
      requiredFields: ['client_id', 'client_secret'],
      optionalFields: ['access_token', 'refresh_token']
    },
    amazon: {
      name: 'Amazon Marketplace',
      supportsOAuth: true,
      oauthUrl: 'https://sellercentral.amazon.com/apps/authorize/consent',
      requiredFields: ['client_id', 'client_secret', 'refresh_token'],
      optionalFields: ['access_token']
    },
    shopify: {
      name: 'Shopify',
      supportsOAuth: true,
      oauthUrl: 'https://partners.shopify.com/oauth/authorize',
      requiredFields: ['shop_domain', 'access_token'],
      optionalFields: ['api_key', 'api_secret']
    },
    tiendanube: {
      name: 'Tiendanube',
      supportsOAuth: true,
      oauthUrl: 'https://www.tiendanube.com/apps/authorize',
      requiredFields: ['client_id', 'client_secret'],
      optionalFields: ['access_token', 'refresh_token']
    },
    prestashop: {
      name: 'PrestaShop',
      supportsOAuth: false,
      oauthUrl: '',
      requiredFields: ['domain', 'api_key'],
      optionalFields: []
    },
    magento: {
      name: 'Magento',
      supportsOAuth: true,
      oauthUrl: 'https://your-magento-store.com/oauth/authorize',
      requiredFields: ['domain', 'client_id', 'client_secret'],
      optionalFields: ['access_token', 'refresh_token']
    },
    wix: {
      name: 'Wix',
      supportsOAuth: true,
      oauthUrl: 'https://www.wix.com/apps/oauth/authorize',
      requiredFields: ['client_id', 'client_secret'],
      optionalFields: ['access_token', 'refresh_token']
    },
    bigcommerce: {
      name: 'BigCommerce',
      supportsOAuth: true,
      oauthUrl: 'https://login.bigcommerce.com/oauth2/authorize',
      requiredFields: ['store_hash', 'client_id', 'client_secret'],
      optionalFields: ['access_token', 'refresh_token']
    },
    vtex: {
      name: 'VTEX',
      supportsOAuth: false,
      oauthUrl: '',
      requiredFields: ['account', 'api_key'],
      optionalFields: ['api_token']
    }
  };

  const config = platform ? platformConfigs[platform.id as keyof typeof platformConfigs] : null;

  const handleOAuthConnection = async () => {
    if (!platform || !config || !config.supportsOAuth) {
      setError('Esta plataforma no soporta autenticación OAuth');
      return;
    }

    // Para Shopify, validar el dominio
    if (platform.id === 'shopify' && !credentials.shopDomain) {
      setError('Por favor ingresa el dominio de tu tienda Shopify');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`🔗 Iniciando OAuth REAL para ${platform.name}...`);
      
      // Iniciar OAuth según la plataforma (sin necesidad de Client ID/Secret)
      let initResult;
      
      switch (platform.id) {
        case 'mercadolibre':
          initResult = await oauthService.initMercadoLibreOAuth();
          break;
        case 'shopify':
          initResult = await oauthService.initShopifyOAuth(credentials.shopDomain);
          break;
        case 'tiendanube':
          initResult = await oauthService.initTiendaNubeOAuth();
          break;
        default:
          setError(`OAuth no implementado para ${platform.name} aún`);
          setLoading(false);
          return;
      }

      if (!initResult.success || !initResult.authUrl) {
        setError(initResult.message || 'Error iniciando OAuth');
        setLoading(false);
        return;
      }

      // Abrir ventana de OAuth
      const oauthWindow = oauthService.openOAuthPopup(initResult.authUrl, platform.id);

      if (!oauthWindow) {
        setError('No se pudo abrir la ventana de autenticación. Verifica que los pop-ups estén habilitados.');
        setLoading(false);
        return;
      }

      // Escuchar el callback OAuth
      const cleanup = oauthService.listenForOAuthCallback(
        platform.id,
        (result) => {
          console.log('✅ OAuth completado exitosamente:', result);
          setLoading(false);
          onConnectionSuccess(platform.id, result.data);
          onClose();
        },
        (error) => {
          console.error('❌ Error en OAuth:', error);
          setError(error || 'Error completando autenticación');
          setLoading(false);
          onConnectionError?.();
        }
      );

      // Verificar si la ventana se cerró prematuramente
      const checkWindow = setInterval(() => {
        if (oauthWindow.closed) {
          clearInterval(checkWindow);
          cleanup();
          if (loading) {
            setError('El proceso de autenticación fue cancelado');
            setLoading(false);
            onConnectionError?.();
          }
        }
      }, 1000);

    } catch (error) {
      console.error('Error en OAuth:', error);
      setError(error instanceof Error ? error.message : 'Error al conectar con la plataforma');
      setLoading(false);
      onConnectionError?.();
    }
  };

  const handleApiKeyConnection = async () => {
    if (!platform || !config) return;

    setLoading(true);
    setError(null);

    try {
      // Validar campos requeridos
      const requiredFields = config.requiredFields;
      const missingFields = requiredFields.filter(field => {
        // Mapear nombres de campos de configuración a nombres del estado
        const stateField = field === 'client_id' ? 'clientId' : 
                          field === 'client_secret' ? 'clientSecret' :
                          field === 'api_key' ? 'apiKey' :
                          field === 'shop_domain' ? 'shopDomain' :
                          field === 'store_hash' ? 'storeHash' :
                          field;
        const value = credentials[stateField as keyof typeof credentials];
        return !value || value.trim() === '';
      });

      if (missingFields.length > 0) {
        setError(`Faltan campos requeridos: ${missingFields.join(', ')}`);
        setLoading(false);
        return;
      }

      // Simular validación de credenciales
      console.log(`🔑 Validando API Keys para ${platform.name}...`);
      
      // Simular delay de validación
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Simular validación exitosa
      console.log('🔑 Credenciales a enviar:', credentials);
      console.log('🔑 Platform ID:', platform.id);
      onConnectionSuccess(platform.id, credentials);
      setLoading(false);
      onClose();

    } catch (error) {
      console.error('Error validando API Keys:', error);
      setError('Error al validar las credenciales. Verifica que sean correctas.');
      setLoading(false);
      onConnectionError?.();
    }
  };

  const toggleSecretVisibility = (field: keyof typeof showSecrets) => {
    setShowSecrets(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const renderOAuthSection = () => (
    <div className="space-y-4">
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
        <div className="flex items-center space-x-2 mb-2">
          <ExternalLink className="w-5 h-5 text-blue-600" />
          <h3 className="font-medium text-blue-900 dark:text-blue-100">
            Conexión OAuth
          </h3>
        </div>
        <p className="text-sm text-blue-700 dark:text-blue-300 mb-4">
          Se abrirá una ventana para autenticarte con {platform?.name}. 
          Una vez autorizada la conexión, se guardarán automáticamente los tokens de acceso.
        </p>
        <button
          onClick={handleOAuthConnection}
          disabled={loading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors duration-200 flex items-center justify-center space-x-2"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Conectando...</span>
            </>
          ) : (
            <>
              <ExternalLink className="w-4 h-4" />
              <span>Conectar con {platform?.name}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderApiKeySection = () => (
    <div className="space-y-4">
      <div className="p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
        <div className="flex items-center space-x-2 mb-4">
          <Key className="w-5 h-5 text-gray-600" />
          <h3 className="font-medium text-gray-900 dark:text-gray-100">
            Credenciales de API
          </h3>
        </div>
        
        <div className="space-y-4">
          {config?.requiredFields.includes('client_id') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Client ID
              </label>
              <input
                type="text"
                value={credentials.clientId}
                onChange={(e) => setCredentials(prev => ({ ...prev, clientId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Ingresa tu Client ID"
              />
            </div>
          )}

          {config?.requiredFields.includes('client_secret') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Client Secret
              </label>
              <div className="relative">
                <input
                  type={showSecrets.clientSecret ? 'text' : 'password'}
                  value={credentials.clientSecret}
                  onChange={(e) => setCredentials(prev => ({ ...prev, clientSecret: e.target.value }))}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Ingresa tu Client Secret"
                />
                <button
                  type="button"
                  onClick={() => toggleSecretVisibility('clientSecret')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {showSecrets.clientSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}

          {config?.requiredFields.includes('api_key') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                API Key
              </label>
              <div className="relative">
                <input
                  type={showSecrets.apiKey ? 'text' : 'password'}
                  value={credentials.apiKey}
                  onChange={(e) => setCredentials(prev => ({ ...prev, apiKey: e.target.value }))}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Ingresa tu API Key"
                />
                <button
                  type="button"
                  onClick={() => toggleSecretVisibility('apiKey')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {showSecrets.apiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}

          {config?.requiredFields.includes('domain') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Dominio de la tienda
              </label>
              <input
                type="text"
                value={credentials.domain}
                onChange={(e) => setCredentials(prev => ({ ...prev, domain: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="ejemplo.com"
              />
            </div>
          )}

          {config?.requiredFields.includes('shop_domain') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Dominio de la tienda Shopify
              </label>
              <input
                type="text"
                value={credentials.shopDomain}
                onChange={(e) => setCredentials(prev => ({ ...prev, shopDomain: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="mi-tienda.myshopify.com"
              />
            </div>
          )}
        </div>

        <button
          onClick={handleApiKeyConnection}
          disabled={loading}
          className="w-full mt-4 bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors duration-200 flex items-center justify-center space-x-2"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Validando...</span>
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              <span>Conectar con API Keys</span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  if (!platform || !config) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{platform.icon}</span>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    Conectar {platform.name}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Configura la conexión para poder publicar productos
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {/* Connection Type Selector */}
              <div className="mb-6">
                <div className="flex space-x-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl">
                  {config.supportsOAuth && (
                    <button
                      onClick={() => setConnectionType('oauth')}
                      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors duration-200 ${
                        connectionType === 'oauth'
                          ? 'bg-white dark:bg-gray-700 text-primary-600 shadow-sm'
                          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                      }`}
                    >
                      OAuth (Recomendado)
                    </button>
                  )}
                  <button
                    onClick={() => setConnectionType('api_key')}
                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors duration-200 ${
                      connectionType === 'api_key'
                        ? 'bg-white dark:bg-gray-700 text-primary-600 shadow-sm'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    API Keys
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                    <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
                  </div>
                </div>
              )}

              {/* Connection Sections */}
              {connectionType === 'oauth' && renderOAuthSection()}
              {connectionType === 'api_key' && renderApiKeySection()}

              {/* Documentation Link */}
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                  ¿Necesitas ayuda para obtener tus credenciales?
                </p>
                <button
                  onClick={() => window.open(`/docs/integraciones/${platform.id}`, '_blank')}
                  className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline"
                >
                  Ver documentación de {platform.name}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PlatformConnectionModal;

